import {
  EFileUploadStatus,
  type IFileUploadEntry,
  type IUploadLimiter,
} from "@frontend-shared/types";
import { createUploadLimiter } from "@frontend-shared/utils";
import { CanceledError, isCancel } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

import { extractServerErrorMessage } from "@/api/utils";

import type { IUseTrackedFileUploadConfig } from "./use-tracked-file-upload.types";

// NOTE: Tracked-upload state machine shared by every file-upload site that
// shows per-file progress + cancel + DONE/ERROR persistence:
//
//   - Knowledge files (org)
//   - Admin Knowledge files
//   - Logos (admin + org, per light/dark dropzone)
//
// Each consumer hook provides the network call, the entry shape (some sites
// extend `IFileUploadEntry` with mode-specific fields), and success
// side-effects via `IUseTrackedFileUploadConfig`. This hook owns the
// `useState`/`useRef` plumbing and the upload-lifecycle (UPLOADING → DONE /
// ERROR / removed-on-cancel) so we don't duplicate that across sites.
//
// Notably distinct from `useUploadVstrAudioFile` which has a CONFIRMING
// phase (presign → S3 PUT → backend confirm) — that flow is left untouched.
export const useTrackedFileUpload = <
  TParams,
  TEntry extends IFileUploadEntry = IFileUploadEntry,
>(
  config: IUseTrackedFileUploadConfig<TParams, TEntry>,
) => {
  const [uploads, setUploads] = useState<TEntry[]>([]);
  const abortControllers = useRef(new Map<string, AbortController>());
  // NOTE: NSAI-7817 — one limiter per hook instance, built on first use, so
  // the cap applies across every batch this hook starts.
  const limiterRef = useRef<IUploadLimiter | null>(null);

  // NOTE: Stable ref to the latest config so `uploadFile` doesn't change
  // identity every render — keeps `useCallback` consumers stable.
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  });

  const updateUpload = useCallback(
    (id: string, update: Partial<IFileUploadEntry>): void => {
      setUploads((prev) =>
        prev.map((entry) =>
          entry.id === id ? { ...entry, ...update } : entry,
        ),
      );
    },
    [],
  );

  const removeUpload = useCallback((id: string): void => {
    setUploads((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const cancelUpload = useCallback((id: string): void => {
    abortControllers.current.get(id)?.abort();
  }, []);

  const cancelAllUploads = useCallback((): void => {
    for (const controller of abortControllers.current.values()) {
      controller.abort();
    }
  }, []);

  const uploadFile = useCallback(
    async (params: TParams): Promise<void> => {
      const cfg = configRef.current;
      const entry = cfg.buildEntry(params);

      // NOTE: NSAI-7817 — a refused file (e.g. over the size cap) fails at
      // once: it never takes a queue slot and its bytes are never sent.
      const rejection = cfg.validate?.(params) ?? null;
      if (rejection) {
        setUploads((prev) => [
          ...prev,
          {
            ...entry,
            status: EFileUploadStatus.ERROR,
            errorMessage: rejection,
          },
        ]);
        return;
      }

      const abortController = new AbortController();
      abortControllers.current.set(entry.id, abortController);

      if (cfg.maxConcurrentUploads && !limiterRef.current) {
        limiterRef.current = createUploadLimiter({
          maxConcurrent: cfg.maxConcurrentUploads,
        });
      }
      const limiter = cfg.maxConcurrentUploads ? limiterRef.current : null;

      setUploads((prev) => [
        ...prev,
        limiter ? { ...entry, isQueued: true } : entry,
      ]);

      const pickedScopeKey = cfg.getScopeKey?.();
      const send = (): Promise<unknown> => {
        // NOTE: NSAI-7817 — see IUseTrackedFileUploadConfig.getScopeKey.
        if (cfg.getScopeKey && cfg.getScopeKey() !== pickedScopeKey) {
          return Promise.reject(
            new CanceledError("The scope changed before the upload started"),
          );
        }
        return cfg.upload(params, {
          signal: abortController.signal,
          onProgress: (percent) =>
            updateUpload(entry.id, { progress: percent }),
        });
      };

      try {
        if (limiter) {
          await limiter.run({
            task: send,
            signal: abortController.signal,
            onStart: () => updateUpload(entry.id, { isQueued: false }),
          });
        } else {
          await send();
        }
        updateUpload(entry.id, {
          status: EFileUploadStatus.DONE,
          progress: 100,
        });
        cfg.onSuccess?.(params);
      } catch (error) {
        // NOTE: axios surfaces aborts as `CanceledError`. `isCancel` is the
        // documented detector and also covers fetch-style `AbortError` if
        // the underlying adapter ever changes — and the limiter's own
        // CanceledError for an upload cancelled while it was still queued.
        if (isCancel(error)) {
          removeUpload(entry.id);
          return;
        }
        const fallback =
          typeof cfg.errorFallback === "function"
            ? cfg.errorFallback()
            : cfg.errorFallback;
        const errorMessage =
          cfg.describeError?.({ params, error }) ??
          extractServerErrorMessage(error) ??
          fallback;
        updateUpload(entry.id, {
          status: EFileUploadStatus.ERROR,
          errorMessage,
          isQueued: false,
        });
      } finally {
        abortControllers.current.delete(entry.id);
      }
    },
    [updateUpload, removeUpload],
  );

  return {
    uploads,
    uploadFile,
    cancelUpload,
    cancelAllUploads,
    removeUpload,
  };
};
